import {
    Wallet,
    ArrowLeftRight,
    IndianRupee,
    Activity,
    ShieldCheck,
    ClipboardCheck,
    MousePointerClick,
    Zap,
    FileCheck2,
    Cpu,
    Lock,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBanner from "@/components/CtaBanner";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const FLOW = [
    { icon: Wallet, title: "Deposit USDT", desc: "Send your USDT through the supported network after starting your exchange on DtOrbit." },
    { icon: ArrowLeftRight, title: "Conversion", desc: "Your USDT is converted to INR based on the live exchange rate shown at the time of your exchange." },
    { icon: IndianRupee, title: "INR Payout", desc: "Once the transaction is checked and processed, INR is paid out through the details you provided." },
];

const FEATURES = [
    { icon: Activity, title: "Live Rates", desc: "The USDT to INR rate is fetched live from DtOrbit, so you always see current pricing." },
    { icon: ShieldCheck, title: "Secure Processing", desc: "Exchanges are handled with security and authenticated processing in mind." },
    { icon: ClipboardCheck, title: "Transaction Tracking", desc: "Follow your exchange status from USDT deposit through INR payout." },
    { icon: MousePointerClick, title: "Simple Experience", desc: "A clean, focused flow with no unnecessary steps." },
    { icon: Zap, title: "Fast Processing", desc: "Built to move your exchange forward quickly once the transaction is confirmed." },
    { icon: FileCheck2, title: "Transparent Information", desc: "Clear rate and exchange details before you commit to anything." },
];

const STEPS = [
    { title: "Visit DtOrbit", desc: "Go to the official DtOrbit website." },
    { title: "Start Exchange", desc: "Begin a new USDT to INR exchange." },
    { title: "Submit USDT", desc: "Send USDT through the supported network." },
    { title: "Confirm Exchange Details", desc: "Your transaction and payout details are checked and confirmed." },
    { title: "Receive INR", desc: "INR is paid out according to the exchange process." },
];

export default function Overview() {
    usePageMeta({
        title: "DtOrbit Overview | USDT to INR Exchange Platform",
        description:
            "A complete overview of the DtOrbit platform — how the USDT to INR exchange works, key features, technology and security approach.",
        path: "/overview",
    });

    return (
        <>
            <PageHero
                testid="overview-hero"
                label="Overview"
                title="DtOrbit"
                highlight="Overview"
                description="A closer look at the DtOrbit ecosystem — a focused platform for USDT to INR exchange with live rates and secure processing."
            />

            <section data-testid="overview-platform" className="relative py-14 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <div className="max-w-3xl">
                            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                                Platform Overview
                            </h2>
                            <p className="mt-5 text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                                The DtOrbit ecosystem is built around one clear
                                purpose: making the exchange of digital assets
                                into Indian Rupees simple and dependable. The
                                platform combines live rate infrastructure, a
                                streamlined exchange flow and transparent
                                processing — so users always know the rate, the
                                steps and the status of their exchange.
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-12">
                        <Reveal>
                            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#0F1B3D]">
                                USDT to INR Exchange
                            </h2>
                        </Reveal>
                        <div className="mt-8 grid md:grid-cols-3 gap-5 md:gap-6">
                            {FLOW.map(({ icon: Icon, title, desc }, i) => (
                                <Reveal key={title} delay={i * 100}>
                                    <div className="glass card-hover rounded-2xl p-6 h-full" data-testid={`overview-flow-${i + 1}`}>
                                        <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E56FF] to-[#3EC6F0] shadow-[0_8px_24px_-8px_rgba(30,86,255,0.6)]">
                                            <Icon className="w-5 h-5 text-white" />
                                        </span>
                                        <h3 className="font-display mt-5 text-lg font-bold text-[#0F1B3D]">
                                            {title}
                                        </h3>
                                        <p className="mt-2 text-sm text-[#5B6B8C] leading-relaxed">
                                            {desc}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section id="features" data-testid="overview-features" className="relative py-14 md:py-20 border-t border-[#E4EAF6] bg-white/60 scroll-mt-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Key Features
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {FEATURES.map(({ icon: Icon, title, desc }, i) => (
                            <Reveal key={title} delay={(i % 3) * 100}>
                                <div className="glass card-hover rounded-2xl p-6 h-full" data-testid={`overview-feature-${i + 1}`}>
                                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#EAF2FF] border border-[#D5E4FF]">
                                        <Icon className="w-5 h-5 text-[#1E56FF]" />
                                    </span>
                                    <h3 className="font-display mt-4 text-base font-bold text-[#0F1B3D]">
                                        {title}
                                    </h3>
                                    <p className="mt-2 text-sm text-[#5B6B8C] leading-relaxed">
                                        {desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid="overview-steps" className="relative py-14 md:py-20 border-t border-[#E4EAF6]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            How It Works
                        </h2>
                    </Reveal>
                    <ol className="mt-10 grid gap-5 md:grid-cols-5 md:gap-4">
                        {STEPS.map((step, i) => (
                            <li key={step.title}>
                                <Reveal delay={i * 90}>
                                    <div className="glass card-hover rounded-2xl p-5 h-full" data-testid={`overview-step-${i + 1}`}>
                                        <span className="inline-flex items-center justify-center min-w-[30px] h-[30px] px-2 rounded-full bg-gradient-to-br from-[#1E56FF] to-[#3EC6F0] text-xs font-extrabold text-white font-display">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <h3 className="font-display mt-3.5 text-sm font-bold text-[#0F1B3D]">
                                            {step.title}
                                        </h3>
                                        <p className="mt-1.5 text-xs text-[#5B6B8C] leading-relaxed">
                                            {step.desc}
                                        </p>
                                    </div>
                                </Reveal>
                            </li>
                        ))}
                    </ol>

                    <div className="mt-12 grid md:grid-cols-2 gap-6">
                        <Reveal>
                            <div className="glass rounded-2xl p-7 h-full" data-testid="overview-technology">
                                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#EAF2FF] border border-[#D5E4FF]">
                                    <Cpu className="w-5 h-5 text-[#1E56FF]" />
                                </span>
                                <h2 className="font-display mt-4 text-xl font-bold text-[#0F1B3D]">
                                    Technology
                                </h2>
                                <p className="mt-2.5 text-sm text-[#5B6B8C] leading-relaxed">
                                    DtOrbit runs on modern web infrastructure
                                    designed for reliability and speed — live
                                    rate feeds, a streamlined exchange
                                    interface and automated processing that
                                    keeps exchanges moving without manual
                                    friction.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={120}>
                            <div className="glass rounded-2xl p-7 h-full" data-testid="overview-security">
                                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#EAF2FF] border border-[#D5E4FF]">
                                    <Lock className="w-5 h-5 text-[#1E56FF]" />
                                </span>
                                <h2 className="font-display mt-4 text-xl font-bold text-[#0F1B3D]">
                                    Security
                                </h2>
                                <p className="mt-2.5 text-sm text-[#5B6B8C] leading-relaxed">
                                    The platform is built with secure session
                                    handling and transaction transparency in
                                    mind — every exchange is checked before
                                    processing, and you can follow its status
                                    end to end. Always use the official
                                    dtorbit.com website for your exchanges.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <CtaBanner />
        </>
    );
}
